import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { trustService } from '../services/api';
import { Shield, Plus, Search, Filter, DollarSign, AlertTriangle, TrendingUp, X } from 'lucide-react';
import NewTrustAccountModal from '../components/trust/NewTrustAccountModal';
import Notification from '../components/common/Notification';
import useNotification from '../hooks/useNotification';

const TrustAccounts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdFilter = searchParams.get('client');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [depositData, setDepositData] = useState({
    account_id: '',
    amount: '',
    description: '',
    check_number: '',
    source: ''
  });
  const [paymentData, setPaymentData] = useState({
    account_id: '',
    amount: '',
    description: '',
    payee: ''
  });
  const [processingTransaction, setProcessingTransaction] = useState(false);
  const { notification, showSuccess, showError, showWarning, hideNotification } = useNotification();

  const fetchTrustAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (clientIdFilter) params.client_id = clientIdFilter;

      const response = await trustService.getTrustAccounts(params);
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Error fetching trust accounts:', error);
      setAccounts([]); // Ensure accounts is always an array
    } finally {
      setLoading(false);
    }
  }, [statusFilter, clientIdFilter]);

  useEffect(() => {
    fetchTrustAccounts();
  }, [fetchTrustAccounts]);

  const filteredAccounts = (Array.isArray(accounts) ? accounts : []).filter(account =>
    account.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.account_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalBalance = (Array.isArray(accounts) ? accounts : []).reduce((sum, account) => sum + parseFloat(account.current_balance || 0), 0);
  const totalAvailable = (Array.isArray(accounts) ? accounts : []).reduce((sum, account) => sum + parseFloat(account.available_balance || 0), 0);
  const activeAccountsCount = (Array.isArray(accounts) ? accounts : []).filter(account => account.status === 'active').length;

  const handleNewDeposit = () => {
    setDepositData({
      account_id: accounts.length > 0 ? accounts[0].id : '',
      amount: '',
      description: '',
      check_number: '',
      source: ''
    });
    setShowDepositModal(true);
  };

  const handleProcessPayment = () => {
    setPaymentData({
      account_id: accounts.length > 0 ? accounts[0].id : '',
      amount: '',
      description: '',
      payee: ''
    });
    setShowPaymentModal(true);
  };

  const handleInterestReport = () => {
    // Generate and download a mock interest report
    const reportData = {
      reportDate: new Date().toISOString().split('T')[0],
      accounts: accounts.map(account => ({
        accountNumber: account.account_number,
        clientName: account.client_name,
        balance: account.current_balance,
        interestRate: '0.10%', // Mock interest rate
        monthlyInterest: (parseFloat(account.current_balance) * 0.001 / 12).toFixed(2)
      })),
      totalInterest: accounts.reduce((sum, account) => sum + (parseFloat(account.current_balance) * 0.001 / 12), 0).toFixed(2)
    };

    const reportContent = `TRUST ACCOUNT INTEREST REPORT\n` +
      `Generated: ${reportData.reportDate}\n\n` +
      `Account Number\tClient Name\tBalance\tMonthly Interest\n` +
      reportData.accounts.map(acc =>
        `${acc.accountNumber}\t${acc.clientName}\t$${parseFloat(acc.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\t$${acc.monthlyInterest}`
      ).join('\n') +
      `\n\nTotal Monthly Interest: $${reportData.totalInterest}`;

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trust-interest-report-${reportData.reportDate}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showSuccess('Interest report generated and downloaded successfully!');
  };

  const handleTrustStatement = () => {
    // Generate and download a mock trust statement
    const statementData = {
      statementDate: new Date().toISOString().split('T')[0],
      totalBalance: totalBalance,
      totalAvailable: totalAvailable,
      activeAccounts: activeAccountsCount,
      accounts: accounts
    };

    const statementContent = `TRUST ACCOUNT STATEMENT\n` +
      `Statement Date: ${statementData.statementDate}\n\n` +
      `SUMMARY\n` +
      `Total Trust Balance: $${statementData.totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `Total Available: $${statementData.totalAvailable.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `Active Accounts: ${statementData.activeAccounts}\n\n` +
      `ACCOUNT DETAILS\n` +
      `Account Number\tClient Name\tStatus\tBalance\tAvailable\n` +
      statementData.accounts.map(acc =>
        `${acc.account_number}\t${acc.client_name}\t${acc.status}\t$${parseFloat(acc.current_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\t$${parseFloat(acc.available_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      ).join('\n');

    const blob = new Blob([statementContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trust-statement-${statementData.statementDate}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showSuccess('Trust statement generated and downloaded successfully!');
  };

  const handleDepositSubmit = async () => {
    try {
      setProcessingTransaction(true);

      // Validate required fields
      if (!depositData.account_id || !depositData.amount || !depositData.description) {
        showWarning('Please fill in all required fields');
        return;
      }

      // Create transaction using the proper API
      const transactionData = {
        trust_account_id: depositData.account_id,
        transaction_type: 'deposit',
        amount: parseFloat(depositData.amount),
        description: depositData.description,
        reference_number: depositData.check_number || null,
        metadata: depositData.source ? { source: depositData.source } : null
      };

      // Call the trust API to create the transaction
      await trustService.createTransaction(transactionData);

      // Reset form and close modal
      setDepositData({
        account_id: '',
        amount: '',
        description: '',
        check_number: '',
        source: ''
      });
      setShowDepositModal(false);

      // Refresh the accounts list to show updated balances
      await fetchTrustAccounts();

      showSuccess('Deposit recorded successfully!');

    } catch (error) {
      console.error('Error processing deposit:', error);
      let errorMessage = 'Failed to process deposit. Please try again.';

      if (error.response?.data?.detail) {
        errorMessage = `Failed to process deposit: ${error.response.data.detail}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid deposit data. Please check your inputs.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Trust account not found.';
      }

      showError(errorMessage);
    } finally {
      setProcessingTransaction(false);
    }
  };

  const handlePaymentSubmit = async () => {
    try {
      setProcessingTransaction(true);

      // Validate required fields
      if (!paymentData.account_id || !paymentData.amount || !paymentData.description || !paymentData.payee) {
        showWarning('Please fill in all required fields');
        return;
      }

      // Create transaction using the proper API (negative amount for withdrawals)
      const transactionData = {
        trust_account_id: paymentData.account_id,
        transaction_type: 'withdrawal',
        amount: -Math.abs(parseFloat(paymentData.amount)), // Ensure negative amount
        description: paymentData.description,
        metadata: { payee: paymentData.payee }
      };

      // Call the trust API to create the transaction
      await trustService.createTransaction(transactionData);

      // Reset form and close modal
      setPaymentData({
        account_id: '',
        amount: '',
        description: '',
        payee: ''
      });
      setShowPaymentModal(false);

      // Refresh the accounts list to show updated balances
      await fetchTrustAccounts();

      showSuccess('Payment recorded successfully!');

    } catch (error) {
      console.error('Error processing payment:', error);
      let errorMessage = 'Failed to process payment. Please try again.';

      if (error.response?.data?.detail) {
        errorMessage = `Failed to process payment: ${error.response.data.detail}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid payment data. Please check your inputs.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Trust account not found.';
      } else if (error.response?.status === 409) {
        errorMessage = 'Insufficient funds for this payment.';
      }

      showError(errorMessage);
    } finally {
      setProcessingTransaction(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Shield className="mr-3 h-8 w-8 text-yellow-600" />
            Trust Accounts
          </h1>
          <p className="text-gray-600 mt-1">
            Manage client trust funds with IOLTA compliance
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>New Trust Account</span>
        </button>
      </div>

      {/* Client Filter Banner */}
      {clientIdFilter && accounts.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-yellow-800">
            Showing trust accounts for <strong>{accounts[0]?.client_name}</strong>
          </span>
          <button
            onClick={() => setSearchParams({})}
            className="text-yellow-600 hover:text-yellow-800 text-sm flex items-center"
          >
            <X className="h-4 w-4 mr-1" />
            Show All
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Trust Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                {trustService.formatCurrency(totalBalance)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Available Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                {trustService.formatCurrency(totalAvailable)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Accounts</p>
              <p className="text-2xl font-bold text-gray-900">{activeAccountsCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Needs Attention</p>
              <p className="text-2xl font-bold text-gray-900">
                {accounts.filter(account => parseFloat(account.available_balance) < 1000).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by client name or account number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
              <option value="frozen">Frozen</option>
            </select>
          </div>
        </div>
      </div>

      {/* Trust Accounts Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Trust Accounts</h3>
        </div>

        {filteredAccounts.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No trust accounts found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating a new trust account.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client & Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Available
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Activity
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAccounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {account.client_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {account.account_number}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {trustService.formatCurrency(account.current_balance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trustService.formatCurrency(account.available_balance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${trustService.getAccountStatusColor(account.status)}`}>
                        {account.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {account.last_transaction_date 
                        ? trustService.formatDate(account.last_transaction_date)
                        : 'No activity'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/trust-accounts/${account.id}`}
                        className="text-yellow-600 hover:text-yellow-700 transition-colors"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={handleNewDeposit}
            className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Plus className="h-5 w-5 text-green-600 mr-3" />
            <span className="text-sm font-medium">New Deposit</span>
          </button>
          <button
            onClick={handleProcessPayment}
            className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <DollarSign className="h-5 w-5 text-yellow-600 mr-3" />
            <span className="text-sm font-medium">Process Payment</span>
          </button>
          <button
            onClick={handleInterestReport}
            className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <TrendingUp className="h-5 w-5 text-purple-600 mr-3" />
            <span className="text-sm font-medium">Interest Report</span>
          </button>
          <button
            onClick={handleTrustStatement}
            className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Shield className="h-5 w-5 text-indigo-600 mr-3" />
            <span className="text-sm font-medium">Trust Statement</span>
          </button>
        </div>
      </div>

      {/* New Trust Account Modal */}
      <NewTrustAccountModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onAccountCreated={() => {
          fetchTrustAccounts();
          setShowCreateModal(false);
        }}
      />

      {/* New Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">New Deposit</h3>
              <button
                onClick={() => setShowDepositModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trust Account *
                </label>
                <select
                  value={depositData.account_id}
                  onChange={(e) => setDepositData(prev => ({ ...prev, account_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">Select an account</option>
                  {accounts.filter(acc => acc.status === 'active').map(account => (
                    <option key={account.id} value={account.id}>
                      {account.client_name} - {account.account_number}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={depositData.amount}
                  onChange={(e) => setDepositData(prev => ({ ...prev, amount: e.target.value }))}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  rows={3}
                  value={depositData.description}
                  onChange={(e) => setDepositData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Describe the source of funds"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Check Number
                </label>
                <input
                  type="text"
                  value={depositData.check_number}
                  onChange={(e) => setDepositData(prev => ({ ...prev, check_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source
                </label>
                <input
                  type="text"
                  value={depositData.source}
                  onChange={(e) => setDepositData(prev => ({ ...prev, source: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="e.g., Settlement, Retainer"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowDepositModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={processingTransaction}
              >
                Cancel
              </button>
              <button
                onClick={handleDepositSubmit}
                disabled={processingTransaction || !depositData.account_id || !depositData.amount || !depositData.description}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {processingTransaction ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Record Deposit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Process Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Process Payment</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trust Account *
                </label>
                <select
                  value={paymentData.account_id}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, account_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">Select an account</option>
                  {accounts.filter(acc => acc.status === 'active').map(account => (
                    <option key={account.id} value={account.id}>
                      {account.client_name} - {account.account_number}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payee *
                </label>
                <input
                  type="text"
                  value={paymentData.payee}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payee: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Who is receiving the payment?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  rows={3}
                  value={paymentData.description}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Purpose of payment"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={processingTransaction}
              >
                Cancel
              </button>
              <button
                onClick={handlePaymentSubmit}
                disabled={processingTransaction || !paymentData.account_id || !paymentData.amount || !paymentData.description || !paymentData.payee}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {processingTransaction ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <DollarSign className="mr-2 h-4 w-4" />
                    Process Payment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default TrustAccounts;